import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Save, Trash2, RefreshCcw, ExternalLink } from 'lucide-react';
import { adminApi, publicApi } from '../../utils/api';
import { InfoTag } from '../../components/ui/InfoTag';

const SECTION_OPTIONS = [
  { id: 'trust', label: 'Trust & Transparency' },
  { id: 'selection', label: 'Tool Selection' },
  { id: 'consulting', label: 'Consulting' },
  { id: 'adoption', label: 'AI Adoption' },
];

const TONE_OPTIONS = ['Direct', 'Honest', 'Warm', 'Confident'];

const EMPTY_FORM = {
  section_id: 'selection',
  audience: 'SMB marketing team',
  use_case: 'AI tool selection',
  pain_point: '',
  primary_tool_slug: '',
  secondary_tool_slug: '',
  tone: 'Direct',
  page_type: 'faq',
  include_stat: true,
  include_disclosure: true,
  include_internal_links: true,
};

function sectionLabelById(sectionId) {
  return SECTION_OPTIONS.find((section) => section.id === sectionId)?.label || 'Tool Selection';
}

export default function FAQAdminPage() {
  const [tools, setTools] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [draft, setDraft] = useState(null);
  const [entryEditor, setEntryEditor] = useState(null);
  const [errorBanner, setErrorBanner] = useState('');

  const sortedTools = useMemo(
    () => [...tools].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [tools]
  );

  const requiredMissing = useMemo(() => {
    const missing = [];
    if (!form.section_id) missing.push('section');
    if (!form.tone) missing.push('tone');
    if (!form.audience?.trim()) missing.push('audience');
    if (!form.use_case?.trim()) missing.push('use case');
    return missing;
  }, [form]);

  const canGenerateDraft = requiredMissing.length === 0;

  const buildFallbackDraft = () => {
    const nowIso = new Date().toISOString();
    const sectionLabel = sectionLabelById(form.section_id);
    const primaryTool = sortedTools.find((tool) => tool.slug === form.primary_tool_slug);
    const secondaryTool = sortedTools.find((tool) => tool.slug === form.secondary_tool_slug);
    const names = [primaryTool?.name, secondaryTool?.name].filter(Boolean);
    const toolPhrase = names.length ? names.join(' vs ') : 'AI tools';
    const question = `Is ${toolPhrase} a good fit for ${form.audience} handling ${form.use_case}?`;

    const answerLines = [
      `For ${form.audience}, ${toolPhrase} can be a strong fit when the top priority is ${form.use_case}.`,
      form.pain_point?.trim()
        ? `Given your pain point (${form.pain_point.trim()}), start with one workflow and validate outcomes for 7-14 days.`
        : 'Start with one workflow and validate outcomes for 7-14 days before wider rollout.',
    ];

    if (form.include_stat) {
      answerLines.push('Teams in this profile usually see 20-30% faster execution after initial setup and onboarding.');
    }

    if (form.include_disclosure) {
      answerLines.push('Disclosure: Wowrolla may earn affiliate commissions from some tools, while fit recommendations remain workflow-first.');
    }

    const tool_links = [primaryTool, secondaryTool]
      .filter(Boolean)
      .map((tool) => ({
        tool_id: tool.id,
        slug: tool.slug,
        name: tool.name,
        url: `/tools/${tool.slug}`,
        anchor: `Read ${tool.name} review`,
      }));

    const internal_links = form.include_internal_links
      ? ['/find', '/compare', '/consulting', ...tool_links.map((entry) => entry.url)]
      : tool_links.map((entry) => entry.url);

    return {
      id: `fallback-${Date.now()}`,
      section_id: form.section_id,
      section_label: sectionLabel,
      tone: form.tone,
      question,
      answer: answerLines.join(' '),
      page_type: form.page_type,
      status: 'draft',
      target_keyword: `${form.use_case} tools for ${form.audience}`.trim(),
      search_intent: 'commercial',
      related_keywords: [form.use_case, form.audience].filter(Boolean),
      entities: names,
      quick_answer: `Yes, if your top goal is ${form.use_case} and you validate with a short pilot first.`,
      tool_links,
      internal_links: [...new Set(internal_links)],
      include_disclosure: form.include_disclosure,
      schema_type: 'FAQPage',
      aeo_ready: true,
      seo_ready: true,
      last_verified: new Date().toISOString().slice(0, 10),
      created_at: nowIso,
      updated_at: nowIso,
    };
  };

  const loadData = async () => {
    setLoading(true);
    setErrorBanner('');
    try {
      const [toolRes, faqRes, publicToolRes] = await Promise.allSettled([
        adminApi.getTools(),
        adminApi.getFaqEntries(),
        publicApi.getAllTools(false),
      ]);

      const adminTools = toolRes.status === 'fulfilled' ? (toolRes.value?.data || []) : [];
      const publicTools = publicToolRes.status === 'fulfilled' ? (publicToolRes.value?.data || []) : [];
      const resolvedTools = adminTools.length ? adminTools : publicTools;

      setTools(resolvedTools);

      if (faqRes.status === 'fulfilled') {
        setEntries((faqRes.value?.data?.items || []).sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || '')));
      } else {
        setEntries([]);
      }

      if (!resolvedTools.length) {
        setErrorBanner('Tool list could not be loaded. Check backend API connection.');
      }

      if (faqRes.status === 'rejected') {
        setErrorBanner((prev) => prev || 'FAQ endpoints are unavailable. You can still generate a local draft and save after backend restart.');
      }
    } catch (error) {
      console.error('Failed loading FAQ admin data', error);
      setErrorBanner('Failed loading FAQ data. Please refresh or check backend server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const onChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const generateDraft = async () => {
    setErrorBanner('');
    if (!canGenerateDraft) {
      setErrorBanner(`Missing required fields: ${requiredMissing.join(', ')}`);
      return;
    }

    setSaving(true);
    try {
      const response = await adminApi.generateFaqEntry(form);
      setDraft(response.data);
    } catch (error) {
      const fallbackDraft = buildFallbackDraft();
      setDraft(fallbackDraft);
      setErrorBanner(
        `Backend FAQ generation failed (${error?.response?.status || 'network'}). A local draft was generated so you can continue.`
      );
    } finally {
      setSaving(false);
    }
  };

  const normalizeEntryPayload = (entry) => ({
    ...entry,
    section_id: entry.section_id || 'selection',
    section_label: entry.section_label || sectionLabelById(entry.section_id),
    tone: entry.tone || 'Direct',
    question: entry.question || '',
    answer: entry.answer || '',
    status: entry.status || 'draft',
    page_type: entry.page_type || 'faq',
    related_keywords: Array.isArray(entry.related_keywords) ? entry.related_keywords : [],
    entities: Array.isArray(entry.entities) ? entry.entities : [],
    tool_links: Array.isArray(entry.tool_links) ? entry.tool_links : [],
    internal_links: Array.isArray(entry.internal_links)
      ? entry.internal_links
      : String(entry.internal_links || '').split(',').map((item) => item.trim()).filter(Boolean),
  });

  const saveDraftToList = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const payload = normalizeEntryPayload(draft);
      const response = await adminApi.createFaqEntry(payload);
      setEntries((prev) => [response.data, ...prev.filter((item) => item.id !== response.data.id)]);
      setDraft(response.data);
      setEntryEditor(response.data);
    } catch (error) {
      alert(error?.response?.data?.detail || 'Failed to save FAQ entry');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (entry) => {
    try {
      const nextStatus = entry.status === 'published' ? 'draft' : 'published';
      const payload = {
        ...entry,
        section_label: entry.section_label || sectionLabelById(entry.section_id),
        question: entry.question,
        answer: entry.answer,
        status: nextStatus,
      };
      const response = await adminApi.updateFaqEntry(entry.id, payload);
      setEntries((prev) => prev.map((item) => (item.id === entry.id ? response.data : item)));
      if (draft?.id === entry.id) {
        setDraft(response.data);
      }
      if (entryEditor?.id === entry.id) {
        setEntryEditor(response.data);
      }
    } catch (error) {
      alert(error?.response?.data?.detail || 'Failed to update FAQ status');
    }
  };

  const startEditEntry = (entry) => {
    setEntryEditor({
      ...entry,
      section_id: entry.section_id || 'selection',
      section_label: entry.section_label || sectionLabelById(entry.section_id),
      internal_links: Array.isArray(entry.internal_links) ? entry.internal_links : [],
    });
  };

  const saveEditedEntry = async () => {
    if (!entryEditor) return;
    setSaving(true);
    try {
      const payload = normalizeEntryPayload(entryEditor);
      const response = payload.id
        ? await adminApi.updateFaqEntry(payload.id, payload)
        : await adminApi.createFaqEntry(payload);
      setEntries((prev) => [response.data, ...prev.filter((item) => item.id !== response.data.id)]);
      setEntryEditor(response.data);
    } catch (error) {
      alert(error?.response?.data?.detail || 'Failed to save FAQ entry changes');
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Delete this FAQ entry?')) return;
    try {
      await adminApi.deleteFaqEntry(entryId);
      setEntries((prev) => prev.filter((entry) => entry.id !== entryId));
      if (draft?.id === entryId) {
        setDraft(null);
      }
      if (entryEditor?.id === entryId) {
        setEntryEditor(null);
      }
    } catch (error) {
      alert(error?.response?.data?.detail || 'Failed to delete FAQ entry');
    }
  };

  if (loading) {
    return <div className="text-sm text-text-secondary">Loading FAQ generator...</div>;
  }

  return (
    <div className="space-y-6" data-testid="faq-admin-page">
      <div className="terminal-grid p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-cyber-yellow">FAQ Generator (SEO + AEO + Tool Links)</h2>
            <InfoTag label="AI-powered FAQ" description="Generate SEO and AEO-optimized FAQ sections from your content targeting featured snippets and voice search" variant="primary" />
          </div>
          <button className="btn-secondary inline-flex items-center gap-2 text-sm" onClick={loadData}>
            <RefreshCcw size={15} /> Refresh
          </button>
        </div>

        <div className="mb-4 p-3 rounded border border-white/10 bg-white/5 text-xs text-text-secondary" data-testid="faq-generator-requirements">
          Required to generate FAQ: section, tone, audience, and use case.
          Optional but recommended: pain point and at least one tool link target.
        </div>

        {errorBanner && (
          <div className="mb-4 p-3 rounded border border-red-500/30 bg-red-500/10 text-xs text-red-300" data-testid="faq-generator-error">
            {errorBanner}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <label className="text-sm text-text-secondary">
            Section
            <select className="input-pro mt-1" value={form.section_id} onChange={(event) => onChange('section_id', event.target.value)}>
              {SECTION_OPTIONS.map((section) => (
                <option key={section.id} value={section.id}>{section.label}</option>
              ))}
            </select>
          </label>

          <label className="text-sm text-text-secondary">
            Tone
            <select className="input-pro mt-1" value={form.tone} onChange={(event) => onChange('tone', event.target.value)}>
              {TONE_OPTIONS.map((tone) => (
                <option key={tone} value={tone}>{tone}</option>
              ))}
            </select>
          </label>

          <label className="text-sm text-text-secondary">
            Audience
            <input className="input-pro mt-1" value={form.audience} onChange={(event) => onChange('audience', event.target.value)} />
          </label>

          <label className="text-sm text-text-secondary">
            Use case
            <input className="input-pro mt-1" value={form.use_case} onChange={(event) => onChange('use_case', event.target.value)} />
          </label>

          <label className="text-sm text-text-secondary md:col-span-2">
            Pain point (marketing context)
            <input className="input-pro mt-1" value={form.pain_point} onChange={(event) => onChange('pain_point', event.target.value)} />
          </label>

          <label className="text-sm text-text-secondary">
            Primary tool link target
            <select className="input-pro mt-1" value={form.primary_tool_slug} onChange={(event) => onChange('primary_tool_slug', event.target.value)}>
              <option value="">None</option>
              {sortedTools.map((tool) => (
                <option key={tool.id || tool.slug || tool.name} value={tool.slug}>{tool.name}</option>
              ))}
            </select>
          </label>

          <label className="text-sm text-text-secondary">
            Secondary tool link target
            <select className="input-pro mt-1" value={form.secondary_tool_slug} onChange={(event) => onChange('secondary_tool_slug', event.target.value)}>
              <option value="">None</option>
              {sortedTools.map((tool) => (
                <option key={tool.id || tool.slug || tool.name} value={tool.slug}>{tool.name}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-4 flex flex-wrap gap-4 text-xs text-text-secondary">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={form.include_stat} onChange={(event) => onChange('include_stat', event.target.checked)} />
            Include measurable stat
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={form.include_disclosure} onChange={(event) => onChange('include_disclosure', event.target.checked)} />
            Include commission disclosure
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={form.include_internal_links} onChange={(event) => onChange('include_internal_links', event.target.checked)} />
            Add internal links
          </label>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button className="btn-primary inline-flex items-center gap-2" onClick={generateDraft} disabled={saving || !canGenerateDraft}>
            <Sparkles size={15} /> Generate FAQ Draft
          </button>
          <button className="btn-secondary inline-flex items-center gap-2" onClick={saveDraftToList} disabled={!draft || saving}>
            <Save size={15} /> Auto-add To FAQ List
          </button>
        </div>
      </div>

      {draft && (
        <div className="terminal-grid p-5">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-base font-semibold text-text-primary">Generated Draft Preview</h3>
            <InfoTag label="Review before saving" description="Review the AI-generated FAQ draft before saving - edit entries to match your editorial voice" variant="primary" />
          </div>
          <p className="text-sm font-semibold text-cyber-yellow">{draft.question}</p>
          <p className="text-sm text-text-secondary mt-2">{draft.answer}</p>
          <div className="mt-3 text-xs text-text-secondary space-y-1">
            <p>SEO keyword: {draft.target_keyword || 'n/a'}</p>
            <p>AEO quick answer: {draft.quick_answer || 'n/a'}</p>
            <p>Tool links: {(draft.tool_links || []).map((link) => link.name).join(', ') || 'none'}</p>
            <p>Internal links: {(draft.internal_links || []).join(', ') || 'none'}</p>
          </div>
        </div>
      )}

      {entryEditor && (
        <div className="terminal-grid p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-text-primary">Edit FAQ Entry</h3>
              <InfoTag label="Existing FAQ editor" description="Selecting an FAQ from the list below hydrates this editor so existing entries can be updated in place" variant="success" />
            </div>
            <div className="flex gap-2">
              <button className="btn-secondary text-xs" onClick={() => setEntryEditor(null)}>Cancel</button>
              <button className="btn-primary text-xs" onClick={saveEditedEntry} disabled={saving || !entryEditor.question || !entryEditor.answer}>Save Changes</button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="text-sm text-text-secondary">
              Section
              <select className="input-pro mt-1" value={entryEditor.section_id || 'selection'} onChange={(event) => setEntryEditor((prev) => ({ ...prev, section_id: event.target.value, section_label: sectionLabelById(event.target.value) }))}>
                {SECTION_OPTIONS.map((section) => (
                  <option key={section.id} value={section.id}>{section.label}</option>
                ))}
              </select>
            </label>

            <label className="text-sm text-text-secondary">
              Tone
              <select className="input-pro mt-1" value={entryEditor.tone || 'Direct'} onChange={(event) => setEntryEditor((prev) => ({ ...prev, tone: event.target.value }))}>
                {TONE_OPTIONS.map((tone) => (
                  <option key={tone} value={tone}>{tone}</option>
                ))}
              </select>
            </label>

            <label className="text-sm text-text-secondary md:col-span-2">
              Question
              <input className="input-pro mt-1" value={entryEditor.question || ''} onChange={(event) => setEntryEditor((prev) => ({ ...prev, question: event.target.value }))} />
            </label>

            <label className="text-sm text-text-secondary md:col-span-2">
              Answer
              <textarea className="input-pro mt-1" rows={5} value={entryEditor.answer || ''} onChange={(event) => setEntryEditor((prev) => ({ ...prev, answer: event.target.value }))} />
            </label>

            <label className="text-sm text-text-secondary">
              Status
              <select className="input-pro mt-1" value={entryEditor.status || 'draft'} onChange={(event) => setEntryEditor((prev) => ({ ...prev, status: event.target.value }))}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </label>

            <label className="text-sm text-text-secondary">
              SEO Keyword
              <input className="input-pro mt-1" value={entryEditor.target_keyword || ''} onChange={(event) => setEntryEditor((prev) => ({ ...prev, target_keyword: event.target.value }))} />
            </label>

            <label className="text-sm text-text-secondary md:col-span-2">
              Internal Links (comma separated)
              <input className="input-pro mt-1" value={Array.isArray(entryEditor.internal_links) ? entryEditor.internal_links.join(', ') : ''} onChange={(event) => setEntryEditor((prev) => ({ ...prev, internal_links: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) }))} />
            </label>
          </div>
        </div>
      )}

      <div className="terminal-grid p-5">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-base font-semibold text-text-primary">FAQ Entries ({entries.length})</h3>
            <InfoTag label="Saved entries" description="Published FAQ entries are rendered on tool and category pages to improve answer engine visibility" variant="primary" />
          </div>
        <div className="space-y-3">
          {entries.map((entry) => (
            <div key={entry.id} className="rounded-lg border border-white/10 p-4 bg-surface">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-xs px-2 py-1 rounded bg-white/5 text-text-secondary">{entry.section_label || sectionLabelById(entry.section_id)}</span>
                <span className="text-xs px-2 py-1 rounded bg-white/5 text-text-secondary">{entry.tone || 'Direct'}</span>
                <span className={`text-xs px-2 py-1 rounded ${entry.status === 'published' ? 'bg-laser-green/20 text-laser-green' : 'bg-white/5 text-text-secondary'}`}>
                  {entry.status || 'draft'}
                </span>
              </div>

              <p className="text-sm font-semibold text-text-primary">{entry.question}</p>
              <p className="text-xs text-text-secondary mt-2">{entry.answer}</p>

              <div className="mt-3 text-xs text-text-secondary space-y-1">
                <p>SEO: {entry.target_keyword || 'n/a'} ({entry.search_intent || 'commercial'})</p>
                <p>AEO entities: {(entry.entities || []).join(', ') || 'none'}</p>
                <p>Tool links: {(entry.tool_links || []).map((toolLink) => toolLink.name).join(', ') || 'none'}</p>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <button className="btn-secondary text-xs" onClick={() => startEditEntry(entry)}>
                  Edit
                </button>
                <button className="btn-secondary text-xs" onClick={() => toggleStatus(entry)}>
                  {entry.status === 'published' ? 'Move to Draft' : 'Publish'}
                </button>
                {(entry.internal_links || []).map((link) => (
                  <Link key={`${entry.id}-${link}`} to={link} className="text-xs inline-flex items-center gap-1 text-cyber-yellow hover:underline">
                    {link} <ExternalLink size={12} />
                  </Link>
                ))}
                <button className="btn-danger text-xs inline-flex items-center gap-1" onClick={() => deleteEntry(entry.id)}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}

          {entries.length === 0 && <p className="text-sm text-text-secondary">No FAQ entries created yet.</p>}
        </div>
      </div>
    </div>
  );
}

